import { KeyboardEvent, MutableRefObject, Ref, useRef } from 'react';
import styled from 'styled-components';

import { ENTER_KEY, LEFT_ARROW, RIGHT_ARROW, SPACE_KEY } from 'constants/keyCodes';
import { spacing } from 'ui/theme';

import noop from 'utils/noop';
import { TEMPLATE_EDITOR_OPTIONS } from '../../modals/constants';
import { TemplateEditorOption } from '../TemplateEditorOption';

const OptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${spacing.double};
  gap: ${spacing.double};
`;

const handleKeyPress = (
  { keyCode }: KeyboardEvent<HTMLDivElement>,
  refs: MutableRefObject<HTMLElement | undefined>[],
) => {
  try {
    const focusedIndex = refs.findIndex((opt) => opt.current === document.activeElement);
    const isItemFocused = focusedIndex > -1;
    const focusedItem = refs[focusedIndex].current;

    switch (keyCode) {
      case RIGHT_ARROW: {
        if (isItemFocused && focusedIndex === 0) {
          refs?.[focusedIndex + 1]?.current?.focus();
        }
        break;
      }

      case LEFT_ARROW: {
        if (isItemFocused && focusedIndex === refs.length - 1) {
          refs?.[focusedIndex - 1]?.current?.focus();
        }
        break;
      }

      case SPACE_KEY:
      case ENTER_KEY: {
        focusedItem?.click();
        break;
      }

      default: {
        return;
      }
    }
  } catch (err) {
    console.error(err); // eslint-disable-line
  }
};

type TemplateEditorSelectorProps = {
  /* content strings to render */
  content: Record<string, any>;
  isWYSIWYGEnabled?: boolean;
  /* Function to select an editor */
  setSelectedEditor: (arg: string) => void;
  /* String of the option selected */
  selectedEditor: string;
};

export const TemplateEditorSelector = ({
  setSelectedEditor = noop,
  selectedEditor,
  content,
  isWYSIWYGEnabled = false,
  ...props
}: TemplateEditorSelectorProps) => {
  const refs = [useRef<HTMLElement>(), useRef<HTMLElement>()];
  return (
    <OptionContainer onKeyDown={(e) => handleKeyPress(e, refs)} {...props}>
      <TemplateEditorOption
        data-test="drag-and-drop-option"
        content={{
          ...TEMPLATE_EDITOR_OPTIONS.DRAG_AND_DROP,
          ...content.dragDrop,
        }}
        selectedEditor={selectedEditor}
        setSelectedEditor={setSelectedEditor}
        ref={refs[0] as Ref<HTMLElement> | undefined}
      />
      {isWYSIWYGEnabled ? (
        <TemplateEditorOption
          data-test="wysiwyg-option"
          content={{
            ...TEMPLATE_EDITOR_OPTIONS.WYSIWYG,
            ...content.wysiwyg,
          }}
          selectedEditor={selectedEditor}
          setSelectedEditor={setSelectedEditor}
          ref={refs[1] as Ref<HTMLElement> | undefined}
        />
      ) : (
        <TemplateEditorOption
          data-test="side-by-side-option"
          content={{
            ...TEMPLATE_EDITOR_OPTIONS.SIDE_BY_SIDE,
            ...content.sideBySide,
          }}
          selectedEditor={selectedEditor}
          setSelectedEditor={setSelectedEditor}
          ref={refs[1] as Ref<HTMLElement> | undefined}
        />
      )}
    </OptionContainer>
  );
};
